/**
 * contractPdfGenerator.ts
 *
 * Génère le PDF d'un contrat d'abonnement en rendant un template HTML structuré, sur le même principe
 * que src/components/Factures/invoicePdfGenerator.ts (iframe caché → html-to-image → jsPDF).
 *
 * Les sections « parties », « prestations », « formule » et « financier » sont ENTIÈREMENT calculées ici
 * depuis les données réelles (contract-data) — seules les 5 zones de texte du modèle (intro, clauses)
 * sont reprises telles quelles, avec {{variables}} déjà remplacées côté données.
 */
import { toPng } from 'html-to-image';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';

/**
 * Lit les dimensions réelles d'un PNG encodé en data URI directement depuis l'en-tête binaire (chunk
 * IHDR, octets 16-23) — même helper que invoicePdfGenerator.ts : le moteur de capture (html-to-image) ne
 * respecte pas toujours fiablement max-height/max-width + object-fit sur des logos au ratio très large.
 */
import { echeancier } from '../../lib/echeancier';
const getPngNaturalSize = (dataUrl: string): { width: number; height: number } | null => {
    try {
        if (!dataUrl.startsWith('data:image/png')) return null;
        const base64 = dataUrl.split(',')[1];
        if (!base64) return null;
        const binary = atob(base64.slice(0, 40));
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const view = new DataView(bytes.buffer);
        const width = view.getUint32(16, false);
        const height = view.getUint32(20, false);
        if (!width || !height) return null;
        return { width, height };
    } catch {
        return null;
    }
};

/** Calcule une taille d'affichage bornée (comme object-fit:contain) mais en pixels explicites. */
const computeBoundedLogoSize = (dataUrl: string, maxWidth: number, maxHeight: number) => {
    const natural = getPngNaturalSize(dataUrl);
    if (!natural) return null;
    const ratio = natural.width / natural.height;
    let width = maxWidth;
    let height = maxWidth / ratio;
    if (height > maxHeight) {
        height = maxHeight;
        width = maxHeight * ratio;
    }
    return { width: Math.round(width), height: Math.round(height) };
};

const fmt = (val: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number.isFinite(val) ? val : 0);
const toDisplayDate = (raw: string) => {
    if (!raw) return '';
    const d = new Date(raw);
    return isNaN(d.getTime()) ? raw : d.toLocaleDateString('fr-FR');
};

export interface ContractData {
    subscription: {
        id: string; status: string; engagement_months: number; start_date: string; end_date: string;
        prix_ttc_mensuel: number; payment_mode: string; signer_name?: string; signed_at?: string;
        formula_name: string; occurrences_used: number; occurrences_total: number; passages_libres?: boolean | number;
    };
    contact: any;
    template: {
        name: string; intro_text: string; clause_duree_renouvellement: string;
        clause_acces_intervention: string; clause_garantie: string; clauses_resiliation: string;
        clause_prestations_supplementaires: string;
    };
    prestataire: {
        company_name: string; address: string; postal_code: string; city: string; country: string;
        siret: string; tva: string; phone: string; capital: string; representant: string; representant_email: string; signature_url?: string;
        logo_url?: string;
    };
    prestations: { label: string; quantity: number; unit_price_ttc: number; line_ttc: number; discount_amount: number; formula_free_quantity?: number; passage_number?: number | null; passage_label?: string | null; list_unit_price_ttc?: number; volume_discount_pct?: number; is_discount_line?: boolean }[];
    passage_totals: { passage_number: number | null; passage_label: string | null; total_ttc: number }[];
    formula_avantages: string[];
    devis: { total_ht: number; total_tva: number; total_ttc: number } | null;
    eligibility_status: { category_name: string; minimum_quantity: number; achieved_quantity: number; met: boolean }[];
    eligibility_by_passage: { passage_number: number | null; passage_label: string | null; categories: { category_name: string; minimum_quantity: number; achieved_quantity: number; met: boolean }[]; amount?: { minimum_ttc: number; achieved_ttc: number; met: boolean } | null; eligible: boolean }[];
    eligibility_mode: 'any' | 'all';
    eligibility_kind?: 'quantite' | 'montant';
    eligibility_minimum_amount_label?: string;
    eligibility_met: boolean;
    all_formulas: { id: string; name: string }[];
    // Clauses du moteur (shared/contratClauses.js), présentes seulement si l'établissement a activé « utiliser ces clauses »
    // pour ce type de client : elles remplacent alors les cinq clauses du modèle. `bloquants` : ce qui empêche l'envoi.
    // `annexe_retractation` : formulaire type (particulier seulement), lignes de texte à joindre en annexe.
    clauses_moteur?: { clauses: { id: string; titre: string; texte: string }[]; bloquants: string[]; annexe_retractation?: string[] | null } | null;
}

const echapperHtml = (s: string) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const contactLabel =(ct: any) => ct?.company_name || `${ct?.first_name || ''} ${ct?.last_name || ''}`.trim() || '';
const varReplace = (text: string, vars: Record<string, string>) =>
    Object.entries(vars).reduce((acc, [k, v]) => acc.split(`{{${k}}}`).join(v), text || '');

export function buildContractHtml(data: ContractData): string {
    const { subscription: sub, contact, template: tpl, prestataire, prestations, passage_totals, formula_avantages, devis, eligibility_by_passage, eligibility_mode, eligibility_met, all_formulas } = data;
    // Seuil en montant (2026-09-08) : remplace le tableau par catégorie par une seule ligne chiffrée.
    const seuilMontantActif = data.eligibility_kind === 'montant' && !!data.eligibility_minimum_amount_label;
    const montantFr = (n: number) => (Number(n) || 0).toFixed(2).replace('.', ',');
    const vars = {
        duree_engagement: `${sub.engagement_months} mois`,
        nom_client: contactLabel(contact),
        formule: sub.formula_name,
        entreprise_nom: prestataire.company_name,
    };
    const moteur = data.clauses_moteur && data.clauses_moteur.clauses.length > 0 ? data.clauses_moteur.clauses : null;
    const annexeRetractation = moteur && data.clauses_moteur?.annexe_retractation?.length ? data.clauses_moteur.annexe_retractation : null;
    const objetMoteur = moteur?.find((c) => c.id === 'objet');
    const introText = objetMoteur ? echapperHtml(objetMoteur.texte) : varReplace(tpl.intro_text, vars);
    const clauseDuree = varReplace(tpl.clause_duree_renouvellement, vars);
    const clauseAcces = varReplace(tpl.clause_acces_intervention, vars);
    const clauseGarantie = varReplace(tpl.clause_garantie, vars);
    const clauseResiliation = varReplace(tpl.clauses_resiliation, vars);
    const clausePrestationsSupp = varReplace(tpl.clause_prestations_supplementaires, vars);

    // Lignes RÉELLEMENT signées par ce client (devis validé dans Suivi de prestation) — jamais la liste
    // générique de la formule : deux clients sur la même formule peuvent avoir des quantités différentes.
    // Séparées par passage comme le devis (2026-09-03, Emeni) : chaque changement de passage_number insère
    // une ligne d'en-tête « Passage N — période ».
    let dernierPassageContrat: number | null | undefined = undefined;
    const prestationsRows = prestations.map((p) => {
        const enTete = p.passage_number !== dernierPassageContrat
            ? (dernierPassageContrat = p.passage_number, p.passage_number != null ? `
        <tr><td colspan="4" style="padding:6px;background:#f8fafc;font-weight:bold;color:#1e40af;">Passage ${p.passage_number}${p.passage_label ? ` — ${p.passage_label}` : ''}</td></tr>` : '')
            : '';
        // Prix unitaire et total AVANT/APRÈS avec le pourcentage de réduction, ancien prix barré au-dessus
        // du nouveau — confirmé « parfait » par Emeni (2026-09-03), ne pas remplacer par un autre format.
        // Ligne d'offre (négative) : libellé et montant seulement, comme sur le devis.
        if (p.is_discount_line) {
            return enTete + `
        <tr>
            <td colspan="3" style="font-style:italic;color:#475569;">${p.label}</td>
            <td style="color:#dc2626;"><b>${fmt(p.line_ttc)}</b></td>
        </tr>`;
        }
        // Prix du catalogue avant toute réduction (lots + formule) : le contrat montre tout ce que le client économise.
        const prixListe = p.list_unit_price_ttc ?? p.unit_price_ttc;
        const totalAvant = prixListe * p.quantity;
        const pct = p.discount_amount > 0 || (p.volume_discount_pct || 0) > 0
            ? (totalAvant > 0 && totalAvant - p.line_ttc > 0.005 ? Math.round(((totalAvant - p.line_ttc) / totalAvant) * 100) : 0) : 0;
        // Ligne partiellement offerte par la formule (plafond en quantité) : on dit « N offertes sur M » et on garde
        // le prix unitaire catalogue — un pourcentage ou un prix moyen n'aurait aucun sens pour le client.
        const offertes = Number(p.formula_free_quantity) || 0;
        const unitApres = offertes > 0 ? p.unit_price_ttc : (p.quantity > 0 ? p.line_ttc / p.quantity : p.unit_price_ttc);
        const mention = offertes > 0
            ? (offertes >= p.quantity ? 'offert' : `${offertes} offertes sur ${p.quantity}`)
            : (pct > 0 ? `-${pct}%${(p.volume_discount_pct || 0) > 0 ? ` dont tarif volume -${p.volume_discount_pct}%` : ''}` : '');
        return enTete + `
        <tr>
            <td>${p.label}${mention ? ` <span style="color:#16a34a;font-weight:bold;">(${mention})</span>` : ''}</td>
            <td>${p.quantity}</td>
            <td>${pct > 0 && offertes === 0 ? `<span style="color:#dc2626;text-decoration:line-through;">${fmt(prixListe)}</span> ` : ''}${fmt(unitApres)}</td>
            <td>${pct > 0 ? `<span style="color:#dc2626;text-decoration:line-through;">${fmt(totalAvant)}</span> ` : ''}<b>${fmt(p.line_ttc)}</b></td>
        </tr>`;
    }).join('');

    // Montant total économisé grâce à la formule (réductions + prestations offertes), demandé par Emeni pour
    // clore visuellement la section « Objet du contrat & Prestations ».
    // Réductions par lots comprises : écart entre le prix du catalogue et le prix réellement facturé.
    // Les lignes « Offert (formule …) » et les remises manuelles sont des lignes négatives à part : on les compte aussi.
    const totalEconomise = prestations.reduce((s, p) => s + (Number(p.discount_amount) || 0)
        + (p.is_discount_line ? (Number((p as any).line_ttc) < 0 ? -Number((p as any).line_ttc) : 0) : Math.max(0, ((p.list_unit_price_ttc ?? p.unit_price_ttc) - p.unit_price_ttc) * p.quantity)), 0);

    // Le tableau financier reprend les totaux RÉELS du devis signé (déjà remisés ligne par ligne), pas un
    // recalcul générique — c'est ce montant précis, par passage, sur lequel le client s'est engagé.
    const montantHt = devis?.total_ht ?? 0;
    const montantTva = devis?.total_tva ?? 0;
    const montantTtcParPassage = devis?.total_ttc ?? sub.prix_ttc_mensuel;

    const formuleCases = all_formulas.map((f) =>
        `[${f.name === sub.formula_name ? 'X' : ' '}] ${f.name.toUpperCase()}`).join('&nbsp;&nbsp;&nbsp;');

    // Le seuil s'évalue PAR PASSAGE, jamais en cumul annuel (2026-09-03, Emeni : « seuil de prestation par
    // passage, et quand c'est atteint on est bon ») — même règle déjà appliquée côté devis. Une mini-table
    // par passage, avec son propre statut ÉLIGIBLE/NON ÉLIGIBLE, plutôt qu'un seul tableau global trompeur.
    const seuilsParPassage = eligibility_by_passage.map((p) => `
        <div class="pdf-avoid-break">
        <p style="font-size:11px;margin:8px 0 2px;"><b>${p.passage_number != null ? `Passage ${p.passage_number}` : 'Passage unique'}${p.passage_label ? ` — ${p.passage_label}` : ''} :</b>
            <span style="color:${p.eligible ? '#16a34a' : '#dc2626'};font-weight:bold;">${p.eligible ? 'ÉLIGIBLE' : 'NON ÉLIGIBLE'}</span>
        </p>
        <table style="width:100%;font-size:11px;border-collapse:collapse;margin-bottom:10px;">
            <thead><tr style="background:#eef2ff;"><th style="text-align:left;padding:6px;border:1px solid #ddd;">${seuilMontantActif ? 'Critère' : 'Catégorie'}</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Minimum requis</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">${seuilMontantActif ? 'Montant atteint' : 'Quantité commandée'}</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Statut</th></tr></thead>
            <tbody>${(seuilMontantActif && p.amount ? [{
                category_name: 'Montant du passage (TTC, avant remise)',
                minimum_quantity: montantFr(p.amount.minimum_ttc) + ' € TTC',
                achieved_quantity: montantFr(p.amount.achieved_ttc) + ' € TTC',
                met: p.amount.met,
            }] : p.categories).map((s: { category_name: string; minimum_quantity: number | string; achieved_quantity: number | string; met: boolean }) => `
            <tr>
                <td style="padding:6px;border:1px solid #ddd;">${s.category_name}</td>
                <td style="padding:6px;border:1px solid #ddd;">${s.minimum_quantity}</td>
                <td style="padding:6px;border:1px solid #ddd;">${s.achieved_quantity}</td>
                <td style="padding:6px;border:1px solid #ddd;color:${s.met ? '#16a34a' : '#dc2626'};font-weight:bold;">${s.met ? 'Atteint' : 'Non atteint'}</td>
            </tr>`).join('')}</tbody>
        </table>
        </div>`).join('');

    const ech = echeancier(devis?.total_ttc, sub.engagement_months, sub.prix_ttc_mensuel);
    const paiementText = sub.payment_mode === 'a_intervention'
        ? `[X] Paiement à l'intervention : facturation après chaque passage (payable à réception).<br>[ ] Mensualisation par prélèvement SEPA.`
        : `[ ] Paiement à l'intervention.<br>[X] Mensualisation par prélèvement SEPA : ${ech.ajustee ? `${ech.nbMois - 1} mensualités de ${fmt(ech.mensualite)} TTC puis une dernière de ${fmt(ech.derniere)} TTC, soit ${fmt(ech.total)} TTC au total.` : `${ech.nbMois} mensualités régulières de ${fmt(ech.mensualite)} TTC / mois.`}`;

    // Logo à la même taille que sur la facture (250×112, cf. invoicePdfGenerator.ts) — demande d'Emeni
    // (2026-09-05) : trop petit/flou avec l'ancien style CSS max-height/max-width, qui n'est pas fiable
    // avec le moteur de capture (html-to-image), comme déjà documenté et corrigé côté facture.
    const logoUrl = prestataire.logo_url ? resolveLogoUrl(prestataire.logo_url) : null;
    const logoSize = logoUrl ? computeBoundedLogoSize(logoUrl, 250, 112) : null;
    const logoStyle = logoSize
        ? `width:${logoSize.width}px;height:${logoSize.height}px;margin-bottom:10px;`
        : 'max-height:112px;max-width:250px;object-fit:contain;margin-bottom:10px;';

    return `
    <div style="width:210mm;min-height:297mm;padding:8mm 20mm 20mm;background:#fff;font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;box-sizing:border-box;">
        ${logoUrl ? `<img src="${logoUrl}" alt="Logo" style="${logoStyle}" />` : ''}
        <h1 style="font-size:20px;color:#1e40af;margin:0 0 4px;">CONTRAT D'ABONNEMENT D'ENTRETIEN ANNUEL</h1>
        <p style="font-size:12px;color:#555;font-style:italic;margin:0 0 20px;">${tpl.name}</p>

        <div class="pdf-avoid-break">
        <h2 style="font-size:14px;color:#1e40af;border-bottom:1px solid #ddd;padding-bottom:4px;">1. IDENTIFICATION DES PARTIES</h2>
        <table style="width:100%;font-size:11px;margin-bottom:16px;">
            <tr>
                <td style="width:50%;vertical-align:top;padding-right:10px;">
                    <b>PRESTATAIRE</b><br>
                    ${prestataire.company_name}<br>
                    ${prestataire.address}, ${prestataire.postal_code} ${prestataire.city}<br>
                    SIRET : ${prestataire.siret || '—'}${prestataire.capital ? ` — Capital : ${prestataire.capital}` : ''}<br>
                    ${prestataire.representant ? `Représenté par : ${prestataire.representant}${prestataire.representant_email ? ` — ${prestataire.representant_email}` : ''}<br>` : ''}
                    Contact : ${prestataire.phone || '—'}
                </td>
                <td style="width:50%;vertical-align:top;">
                    <b>CLIENT${contact?.type === 'PROFESSIONNEL' ? ' PROFESSIONNEL' : ''}</b><br>
                    ${contactLabel(contact)}<br>
                    ${[contact?.address, [contact?.postal_code, contact?.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') || 'Adresse non renseignée'}<br>
                    ${contact?.type === 'PROFESSIONNEL' ? `SIRET : ${contact?.siret || '—'}<br>` : ''}
                    ${contact?.type === 'PROFESSIONNEL' && (contact?.organizer_name || `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim()) ? `Représentée par : ${contact.organizer_name || `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim()}${contact?.organizer_email ? ` — ${contact.organizer_email}` : ''}<br>` : ''}
                    Contact : ${contact?.phone || '—'} | ${contact?.email || '—'}
                </td>
            </tr>
        </table>
        </div>

        <div class="pdf-avoid-break">
        <h2 style="font-size:14px;color:#1e40af;border-bottom:1px solid #ddd;padding-bottom:4px;">2. OBJET DU CONTRAT & PRESTATIONS</h2>
        <p style="font-size:11px;">${introText}</p>
        </div>
        <table style="width:100%;font-size:11px;border-collapse:collapse;margin-bottom:16px;">
            <thead><tr style="background:#eef2ff;"><th style="text-align:left;padding:6px;border:1px solid #ddd;">Prestation</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Quantité</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Prix unitaire</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Total ligne</th></tr></thead>
            <tbody>${prestationsRows || '<tr><td colspan="4" style="padding:6px;border:1px solid #ddd;">Aucun devis signé rattaché à cet abonnement.</td></tr>'}</tbody>
        </table>
        ${totalEconomise > 0 ? `<p style="font-size:11px;text-align:right;margin-bottom:16px;"><b>Montant total économisé grâce à la formule (réductions par lots, réductions de la formule et prestations offertes) :</b> <span style="color:#16a34a;font-weight:bold;">${fmt(totalEconomise)}</span></p>` : ''}

        <div class="pdf-avoid-break">
        <h2 style="font-size:14px;color:#1e40af;border-bottom:1px solid #ddd;padding-bottom:4px;">3. FORMULE & MODALITÉS D'INTERVENTION</h2>
        <p style="font-size:11px;"><b>FORMULE SÉLECTIONNÉE :</b> ${formuleCases}</p>
        <p style="font-size:11px;">
            Durée de l'engagement : ${sub.engagement_months} mois fermes à compter du ${toDisplayDate(sub.start_date)}.<br>
            ${Number(sub.passages_libres)
                ? `Passages : libres, à votre rythme, pendant la durée de validité de la formule.`
                : `Nombre de passages inclus au contrat : ${sub.occurrences_total}.<br>
            Un passage regroupe toutes les journées de travail nécessaires pour réaliser les prestations prévues à ce passage : il compte pour un seul passage, même s'il s'étend sur plusieurs jours.`}
        </p>
        ${formula_avantages.length > 0 ? `
        <p style="font-size:11px;"><b>Avantages de la formule souscrite :</b></p>
        <ul style="font-size:11px;margin:0 0 12px 18px;padding:0;">
            ${formula_avantages.map((a) => `<li>${a}</li>`).join('')}
        </ul>` : ''}
        ${eligibility_by_passage.length > 0 && (seuilMontantActif || eligibility_by_passage.some((p) => p.categories.length > 0)) ? `
        <p style="font-size:11px;"><b>Seuil d'éligibilité à la formule</b> (${seuilMontantActif ? `minimum ${data.eligibility_minimum_amount_label} pour une prestation` : eligibility_mode === 'all' ? 'toutes les catégories ci-dessous doivent être atteintes' : 'une seule des catégories ci-dessous suffit'}), évalué SÉPARÉMENT à chaque passage — statut global du contrat :
            <span style="color:${eligibility_met ? '#16a34a' : '#dc2626'};font-weight:bold;">${eligibility_met ? 'ÉLIGIBLE' : 'NON ÉLIGIBLE'}</span>
        </p>
        ${seuilsParPassage}` : ''}
        </div>

        <div class="pdf-avoid-break">
        <h2 style="font-size:14px;color:#1e40af;border-bottom:1px solid #ddd;padding-bottom:4px;">4. CONDITIONS FINANCIÈRES ET RÈGLEMENT</h2>
        ${passage_totals.length > 1 ? `
        <p style="font-size:11px;"><b>Montant TTC par passage</b> (devis signé, remises formule incluses) :</p>
        <table style="width:100%;font-size:11px;border-collapse:collapse;margin-bottom:10px;">
            <thead><tr style="background:#eef2ff;"><th style="text-align:left;padding:6px;border:1px solid #ddd;">Passage</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Montant TTC</th></tr></thead>
            <tbody>${passage_totals.map((p) => `<tr><td style="padding:6px;border:1px solid #ddd;">${p.passage_number != null ? `Passage ${p.passage_number}` : '—'}${p.passage_label ? ` — ${p.passage_label}` : ''}</td><td style="padding:6px;border:1px solid #ddd;">${fmt(p.total_ttc)}</td></tr>`).join('')}</tbody>
        </table>` : ''}
        <table style="width:100%;font-size:11px;border-collapse:collapse;margin-bottom:10px;">
            <thead><tr style="background:#eef2ff;"><th style="text-align:left;padding:6px;border:1px solid #ddd;">Désignation</th><th style="text-align:left;padding:6px;border:1px solid #ddd;">Montant</th></tr></thead>
            <tbody>
                <tr><td style="padding:6px;border:1px solid #ddd;">Montant HT total (devis signé, ${passage_totals.length || 1} passage${passage_totals.length > 1 ? 's' : ''} sur l'année, remises formule incluses)</td><td style="padding:6px;border:1px solid #ddd;">${fmt(montantHt)}</td></tr>
                <tr><td style="padding:6px;border:1px solid #ddd;">TVA</td><td style="padding:6px;border:1px solid #ddd;">${fmt(montantTva)}</td></tr>
                <tr style="font-weight:bold;"><td style="padding:6px;border:1px solid #ddd;">TOTAL TTC SUR L'ANNÉE</td><td style="padding:6px;border:1px solid #ddd;">${fmt(montantTtcParPassage)}</td></tr>
                ${sub.payment_mode === 'a_intervention'
                    // BUG TROUVÉ (2026-09-06, Emeni) : cette ligne affichait un "MONTANT MENSUEL CONTRACTUEL
                    // ... / mois" quel que soit le mode de paiement — en contradiction directe avec la case
                    // "[X] Paiement à l'intervention" cochée juste en dessous, alors qu'aucune mensualité
                    // n'existe dans ce mode (la facturation suit chaque passage).
                    ? `<tr style="font-weight:bold;"><td style="padding:6px;border:1px solid #ddd;">FACTURATION</td><td style="padding:6px;border:1px solid #ddd;">Après chaque passage, selon les montants ci-dessus</td></tr>`
                    : `<tr style="font-weight:bold;"><td style="padding:6px;border:1px solid #ddd;">MONTANT MENSUEL CONTRACTUEL</td><td style="padding:6px;border:1px solid #ddd;">${fmt(ech.mensualite)} TTC / mois${ech.ajustee ? ` (dernière mensualité : ${fmt(ech.derniere)})` : ''}</td></tr>`}
            </tbody>
        </table>
        <p style="font-size:11px;"><b>Modalités de paiement :</b><br>${paiementText}</p>
        </div>

        <div class="pdf-avoid-break">
        <h2 style="font-size:14px;color:#1e40af;border-bottom:1px solid #ddd;padding-bottom:4px;">5. CONDITIONS GÉNÉRALES ET ENGAGEMENT</h2>
        ${moteur
            ? moteur.filter((c) => c.id !== 'objet').map((c) => `<p style="font-size:11px;"><b>${echapperHtml(c.titre)} :</b> ${echapperHtml(c.texte)}</p>`).join('\n        ')
            : `<p style="font-size:11px;"><b>Durée et renouvellement :</b> ${clauseDuree}</p>
        <p style="font-size:11px;"><b>Résiliation anticipée :</b> ${clauseResiliation}</p>
        <p style="font-size:11px;"><b>Accès et conditions d'intervention :</b> ${clauseAcces}</p>
        <p style="font-size:11px;"><b>Garantie et réclamations :</b> ${clauseGarantie}</p>
        <p style="font-size:11px;"><b>Prestations supplémentaires :</b> ${clausePrestationsSupp}</p>`}
        </div>

        <div class="pdf-avoid-break">
        <h2 style="font-size:14px;color:#1e40af;border-bottom:1px solid #ddd;padding-bottom:4px;">6. SIGNATURE ET ACCEPTATION DES PARTIES</h2>
        <p style="font-size:11px;">Fait à ${prestataire.city || '—'}, le ${toDisplayDate(sub.signed_at || new Date().toISOString())}, en deux exemplaires originaux.</p>
        <table style="width:100%;font-size:11px;border-collapse:collapse;">
            <tr>
                <td style="width:50%;border:1px solid #ddd;padding:10px;vertical-align:top;"><b>Pour le Prestataire :</b><br>${prestataire.company_name}${prestataire.representant ? `<br>${prestataire.representant}` : ''}<br><br>${prestataire.signature_url ? `Signature :<br><img src="${resolveLogoUrl(prestataire.signature_url)}" alt="Signature" style="max-height:60px;max-width:180px;object-fit:contain;margin-top:4px;" />` : 'Signature :'}</td>
                <td style="width:50%;border:1px solid #ddd;padding:10px;vertical-align:top;"><b>Pour le Client :</b><br>${contactLabel(contact)}<br><br>${sub.signed_at ? `Signé électroniquement par ${sub.signer_name || ''} le ${toDisplayDate(sub.signed_at)}` : 'Signature :'}</td>
            </tr>
        </table>
        </div>
        ${annexeRetractation ? `<div class="pdf-avoid-break" style="margin-top:18px;">
        <h2 style="font-size:14px;color:#1e40af;border-bottom:1px solid #ddd;padding-bottom:4px;">ANNEXE : FORMULAIRE DE RÉTRACTATION</h2>
        ${annexeRetractation.map((l, i) => `<p style="font-size:11px;${i === 0 ? 'font-weight:bold;' : ''}">${echapperHtml(l)}</p>`).join('\n        ')}
        </div>` : ''}
    </div>`;
}

export async function generateContractPdfFromData(data: ContractData): Promise<{ pdf: any; blob: Blob }> {
    const htmlContent = buildContractHtml(data);

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '210mm';
    // Assez haut pour ne jamais brider un contrat multi-page (contenu réel mesuré ensuite via scrollHeight) —
    // même valeur que le devis, un contrat court n'en est pas affecté.
    iframe.style.height = '3000mm';
    iframe.style.border = 'none';
    iframe.style.zIndex = '-1';
    document.body.appendChild(iframe);

    await new Promise<void>((resolve) => {
        iframe.onload = () => resolve();
        iframe.srcdoc = htmlContent;
        setTimeout(resolve, 2000);
    });

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    // BUG TROUVÉ (2026-09-03, Emeni : « je ne vois pas le logo ») : le logo et la signature sont des images
    // (parfois volumineuses, base64) jamais attendues avant la capture — toPng() pouvait photographier la
    // page avant que l'image ait fini de se décoder, la laissant vide. Même attente que le devis/la facture.
    const imgsToWait = Array.from(iframeDoc?.querySelectorAll('img') || []);
    if (imgsToWait.length > 0) {
        await Promise.all(imgsToWait.map((img) => new Promise<void>((res) => {
            if (img.complete) { res(); return; }
            img.onload = () => res();
            img.onerror = () => res();
            setTimeout(res, 1500);
        })));
    }
    // BUG TROUVÉ (2026-09-05, Emeni : « le logo ne s'affiche pas sur iPhone », sur le devis avec le même
    // mécanisme) : computeBoundedLogoSize ne sait lire que les PNG (chunk IHDR) — pour tout autre format
    // (JPEG...), le logo retombe sur max-height/max-width + object-fit:contain, que Safari iOS ne restitue
    // pas de façon fiable dans le <foreignObject> SVG de html-to-image (disparaît silencieusement). Filet de
    // sécurité : si l'image logo n'a pas de largeur/hauteur explicites en pixels (donc encore sur
    // object-fit), on les calcule ici depuis ses dimensions réelles une fois chargée — fonctionne quel que
    // soit le format.
    const logoImgEl = iframeDoc?.querySelector<HTMLImageElement>('img[alt="Logo"]');
    if (logoImgEl && !logoImgEl.style.width && logoImgEl.naturalWidth && logoImgEl.naturalHeight) {
        const maxWidth = 250, maxHeight = 112;
        const ratio = logoImgEl.naturalWidth / logoImgEl.naturalHeight;
        let width = maxWidth, height = maxWidth / ratio;
        if (height > maxHeight) { height = maxHeight; width = maxHeight * ratio; }
        logoImgEl.style.width = `${Math.round(width)}px`;
        logoImgEl.style.height = `${Math.round(height)}px`;
        logoImgEl.style.maxWidth = '';
        logoImgEl.style.maxHeight = '';
        logoImgEl.style.objectFit = '';
    }

    const targetEl = iframeDoc?.body?.firstElementChild as HTMLElement | null;
    if (!targetEl) {
        document.body.removeChild(iframe);
        throw new Error('Impossible de rendre le contrat.');
    }

    // Attend que les polices de l'iframe soient chargées AVANT de mesurer offsetTop/offsetHeight — sinon la
    // police de repli (plus large/étroite) est utilisée pour la mesure, décalant les zones interdites par
    // rapport à la position réelle dans l'image capturée par toPng() juste après.
    if (iframe.contentWindow && (iframe.contentWindow as Window & { document: Document }).document.fonts) {
        try { await (iframe.contentWindow as Window & { document: Document }).document.fonts.ready; } catch { /* ignore */ }
    }

    // Zones interdites à la découpe — demande d'Emeni (2026-09-03) : « on ne coupe aucun paragraphe, aucun
    // petit point, si la page ne permet pas de tout prendre ». Chaque ligne de tableau (<tr>), chaque
    // paragraphe/clause (<p>) et chaque puce (<li>) est protégé individuellement — jamais le bloc entier qui
    // les contient (une table ou une liste peut largement dépasser une page), seulement l'unité elle-même,
    // qui ne doit jamais être scindée en deux — plus les petits blocs complets (.pdf-avoid-break : parties,
    // section financière, seuil par passage, signature). Mesurées en mm dans l'espace de la page AVANT
    // capture (offsetTop/offsetHeight relatifs à targetEl), indépendant du pixelRatio de la capture.
    const cssScale = 210 / targetEl.scrollWidth;
    const forbiddenEls = [
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('table')),
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('tr')),
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('p')),
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('li')),
        ...Array.from(targetEl.querySelectorAll<HTMLElement>('.pdf-avoid-break')),
    ];
    // Demande d'Emeni (2026-09-03) : une <table> entière est protégée en priorité (jamais coupée si elle
    // tient sur une page fraîche) — seul un tableau plus haut qu'une page entière retombe sur la protection
    // ligne par ligne (<tr>) déjà en place, pour ne jamais boucler indéfiniment.
    // BUG TROUVÉ (2026-09-03, Emeni : « la section 4 est coupée en deux ») : offsetTop est relatif à
    // offsetParent, pas à la page — pour un <tr>, offsetParent est souvent la <table> elle-même (voire une
    // cellule), pas targetEl. Les zones interdites des lignes de tableau étaient donc mesurées à une position
    // fausse, ce qui faussait la comparaison "top minimal" avec les blocs englobants (.pdf-avoid-break, la
    // section 4 entière) et laissait passer des coupures à l'intérieur de sections censées être protégées.
    // getBoundingClientRect() donne une position fiable quelle que soit l'imbrication (table, cellule...).
    const targetRectTop = targetEl.getBoundingClientRect().top;
    // BUG TROUVÉ (2026-09-04, Emeni : « le devis a plusieurs pages blanches » — même code partagé avec le
    // contrat) : un <table> (ou tout autre bloc) plus haut qu'une page entière ne peut JAMAIS être protégé en
    // bloc — l'algorithme essayait quand même de reculer jusqu'à son début à chaque page, produisant une page
    // quasi vide juste avant d'abandonner. Ignoré dès la mesure : seul un bloc qui PEUT réellement tenir sur
    // une page fraîche mérite d'être protégé en entier.
    const MAX_ATOMIC_BLOCK_HEIGHT_MM = 297 - 20 - 20; // pageHeight - marges haut/bas (constantes redéclarées plus bas, cf. pageBudget)
    const forbiddenRanges = forbiddenEls.map((el) => {
        const rect = el.getBoundingClientRect();
        return { top: (rect.top - targetRectTop) * cssScale, bottom: (rect.bottom - targetRectTop) * cssScale, isTable: el.tagName.toLowerCase() === 'table', tag: el.tagName + (el.className ? '.' + el.className : '') + ':' + (el.textContent || '').slice(0, 25) };
    }).filter((r) => (r.bottom - r.top) <= MAX_ATOMIC_BLOCK_HEIGHT_MM);
    // BUG TROUVÉ (2026-09-03, Emeni : « le petit 3 est coupé » — en fait le titre "3. FORMULE & MODALITÉS..."
    // se retrouvait seul en bas de page, tout son contenu partant sur la page suivante). Rien ne protégeait
    // un titre <h2> d'être séparé de ce qui le suit immédiatement. On étend sa zone protégée jusqu'au début
    // de l'élément suivant (+ une marge d'une ligne) pour forcer le titre entier à basculer avec le début de
    // son contenu si la page ne peut accueillir au moins un peu des deux ensemble.
    const headingKeepWithNext = Array.from(targetEl.querySelectorAll<HTMLElement>('h2')).map((h2) => {
        const rect = h2.getBoundingClientRect();
        const top = (rect.top - targetRectTop) * cssScale;
        let bottom = (rect.bottom - targetRectTop) * cssScale;
        const next = h2.nextElementSibling as HTMLElement | null;
        if (next) {
            const nextRect = next.getBoundingClientRect();
            bottom = Math.max(bottom, (nextRect.top - targetRectTop) * cssScale + 8);
        }
        return { top, bottom, isTable: false, tag: 'H2-keepnext:' + (h2.textContent || '').slice(0, 25) };
    });
    forbiddenRanges.push(...headingKeepWithNext);
    if ((window as any).__PDF_DEBUG__) console.log('[pdf-debug] all ranges', forbiddenRanges.map(r => ({ top: Math.round(r.top * 10) / 10, bottom: Math.round(r.bottom * 10) / 10, tag: (r as any).tag })));

    try {
        // Ratio de capture plafonné pour rester sous la limite de taille de canvas des navigateurs mobiles
        // (Safari iOS notamment : un canvas trop grand — long contrat multi-page à pixelRatio fixe 1.5 —
        // produit une image vide/tronquée, sans erreur visible : bug remonté par Emeni, "PDF vide/cassé sur
        // téléphone" alors qu'il s'affichait bien sur ordinateur). 4000px de côté reste largement sous les
        // limites connues (~4096px ou ~16 Mpx selon l'appareil) tout en gardant 1.5 sur desktop/contrats courts.
        const safePixelRatio = Math.min(1.5, 4000 / Math.max(targetEl.scrollWidth, targetEl.scrollHeight, 1));
        const imgData = await toPng(targetEl, { cacheBust: true, pixelRatio: safePixelRatio, backgroundColor: '#ffffff', width: targetEl.scrollWidth, height: targetEl.scrollHeight });
        const { jsPDF } = await import('jspdf');
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageWidth = 210;
        const pageHeight = 297;
        const imageProps = pdf.getImageProperties(imgData);
        // BUG TROUVÉ (2026-09-03, signalé par Emeni avec un PDF téléchargé à l'appui) : Math.min sur largeur
        // ET hauteur compressait tout le contrat pour qu'il tienne sur UNE page, laissant une grande marge
        // vide à droite dès que le contenu dépassait une page — même bug déjà corrigé sur le devis
        // (devisPrestationPdfGenerator.ts). Mise à l'échelle sur la LARGEUR seulement, jamais la hauteur.
        const scale = pageWidth / imageProps.width;
        const imageWidth = imageProps.width * scale;
        const imageHeight = imageProps.height * scale;

        // Pagination consciente des tableaux : au lieu de couper tous les 297mm pile, le point de coupure
        // recule jusqu'au début du bloc "pdf-avoid-break" en cours si la coupure naïve tombait dedans — un
        // tableau (ou toute la section 4) n'est ainsi jamais scindé entre deux pages.
        // BUG TROUVÉ (2026-09-03, Emeni : « le devis/contrat est coupé », puis un contenu qui semblait
        // dupliqué d'une page à l'autre) : jsPDF addImage() peint TOUJOURS un plein rectangle pageHeight,
        // quelle que soit la hauteur "voulue" — reculer `breakAt` pour protéger un bloc ne l'empêchait donc
        // pas d'apparaître quand même en bas de la page courante (l'image complète était repeinte à chaque
        // fois, seul le point de départ de la page SUIVANTE changeait) : le contenu entre le point protégé et
        // le bas de la page se retrouvait affiché deux fois. Chaque page utilise désormais une image RECADRÉE
        // à sa tranche exacte (via un canvas), jamais l'image complète repositionnée.
        const sourceImg = new Image();
        await new Promise<void>((resolve, reject) => { sourceImg.onload = () => resolve(); sourceImg.onerror = () => reject(new Error('Image de rendu invalide.')); sourceImg.src = imgData; });
        const pxPerMm = imageProps.width / imageWidth;

        // BUG TROUVÉ (2026-09-03, Emeni : « la marge en haut de page est trop étroite sur les pages 2 et 3 »)
        // : seule la première page a une marge en haut, car elle est incluse dans le contenu capturé
        // (padding:20mm du conteneur) — les pages suivantes démarrent une tranche recadrée en plein milieu du
        // document, sans aucune marge. On réserve donc un blanc de 20mm en haut de chaque page à partir de la 2e,
        // et un blanc de 20mm en bas de CHAQUE page (y compris la 1re si elle n'est pas la dernière) — sans quoi
        // le contenu peut aller jusqu'au bord exact de la page suivante.
        const CONTINUATION_TOP_MARGIN_MM = 20;
        const BOTTOM_MARGIN_MM = 20;
        let usedHeight = 0;
        let firstPage = true;
        while (usedHeight < imageHeight - 0.5) {
            const pageBudget = (firstPage ? pageHeight : pageHeight - CONTINUATION_TOP_MARGIN_MM) - BOTTOM_MARGIN_MM;
            const naiveBreak = Math.min(usedHeight + pageBudget, imageHeight);
            // Retient la collision la plus proche (top minimal), jamais la première trouvée dans l'ordre du DOM.
            let breakAt = naiveBreak;
            for (const r of forbiddenRanges) {
                if (naiveBreak > r.top && naiveBreak < r.bottom && r.top > usedHeight + 1 && r.top < breakAt) {
                    // Même correctif que devisPrestationPdfGenerator.ts / invoicePdfGenerator.ts (2026-09-05,
                    // Emeni : « page 1 quasi vide ») : un <table> entier n'est reporté en bloc à la page
                    // suivante que si ça ne gâche pas plus de MAX_WASTED_SPACE_MM sur la page en cours.
                    const MAX_WASTED_SPACE_MM = 60;
                    if (r.isTable && (r.top - usedHeight) > MAX_WASTED_SPACE_MM) continue;
                    breakAt = r.top;
                }
            }
            if ((window as any).__PDF_DEBUG__) console.log('[pdf-debug] page break', { usedHeight, naiveBreak, breakAt, ranges: forbiddenRanges.filter(r => naiveBreak > r.top && naiveBreak < r.bottom && r.top > usedHeight + 1).map(r => ({ top: r.top, bottom: r.bottom })) });
            // BUG TROUVÉ (2026-09-03, Emeni : un filet de texte flou reste visible au bas de la page qui
            // précède un bloc protégé) : couper l'image PILE au pixel où commence le bloc protégé laisse
            // parfois échapper l'antialiasing du haut d'une lettre. Petite marge de sécurité pour finir
            // chaque page sur du blanc franc avant le bloc protégé suivant.
            // BUG TROUVÉ (2026-09-04, Emeni : « le devis a plusieurs pages blanches » — même code partagé) :
            // quand plusieurs éléments protégés sont très rapprochés, reculer de 4mm PUIS forcer un plancher à
            // usedHeight+1 ne faisait avancer que d'1mm par page — plusieurs pages quasi vides de suite. On
            // n'applique la marge que si elle laisse un progrès réel ; sinon on renonce à la marge plutôt
            // qu'au progrès.
            if (breakAt < naiveBreak) {
                const withMargin = breakAt - 4;
                if (withMargin > usedHeight + 1) breakAt = withMargin;
            }
            if (breakAt <= usedHeight) breakAt = usedHeight + pageBudget; // bloc plus haut qu'une page : coupe quand même, jamais de boucle infinie

            const sliceHeightMm = breakAt - usedHeight;
            const srcY = Math.round(usedHeight * pxPerMm);
            const srcH = Math.max(1, Math.round(sliceHeightMm * pxPerMm));
            const canvas = document.createElement('canvas');
            canvas.width = imageProps.width;
            canvas.height = srcH;
            const ctx = canvas.getContext('2d')!;
            ctx.drawImage(sourceImg, 0, srcY, imageProps.width, srcH, 0, 0, imageProps.width, srcH);
            const sliceData = canvas.toDataURL('image/png');

            if (!firstPage) pdf.addPage();
            pdf.addImage(sliceData, 'PNG', 0, firstPage ? 0 : CONTINUATION_TOP_MARGIN_MM, imageWidth, sliceHeightMm, undefined, 'SLOW');
            usedHeight = breakAt;
            firstPage = false;
        }
        const blob = pdf.output('blob');
        return { pdf, blob };
    } finally {
        document.body.removeChild(iframe);
    }
}

export async function generateContractPdfBase64(data: ContractData): Promise<string> {
    // Utilisé pour ENVOYER le contrat : avec les clauses du moteur, on n'envoie pas un contrat incomplet (médiateur manquant…).
    if (data.clauses_moteur?.bloquants?.length) throw new Error(`Le contrat n'est pas prêt à partir : ${data.clauses_moteur.bloquants.join(' ')}`);
    const { blob } = await generateContractPdfFromData(data);
    return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
